import type { ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, type LucideIcon } from 'lucide-react';
import { SOVEREIGN } from '../../../utils/epcProjectVisual';
import { BanknoteRule, TILE } from '../ui';

export default function SovereignModal({
  open,
  onClose,
  title,
  subtitle,
  icon: Icon,
  maxWidthClass = 'max-w-3xl',
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon: LucideIcon;
  maxWidthClass?: string;
  children: ReactNode;
  footer: ReactNode;
}) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="sv-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(42,31,23,0.35)', backdropFilter: 'blur(3px)' }}
            onClick={onClose}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4 sm:p-6" onClick={onClose}>
            <motion.div
              key="sv-panel"
              role="dialog"
              aria-modal="true"
              aria-label={title}
              initial={{ opacity: 0, y: 14, scale: 0.985 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.99 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className={`relative my-auto flex max-h-[92vh] w-full ${maxWidthClass} flex-col overflow-hidden rounded-2xl`}
              style={{
                background: 'rgba(250,247,238,0.99)',
                border: '1px solid rgba(11,59,46,0.35)',
                boxShadow: '0 18px 44px -20px rgba(42,31,23,0.40)',
              }}
            >
              <BanknoteRule />
              <div className="relative flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid rgba(11,59,46,0.14)' }}>
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl text-white shadow-sm" style={{ background: TILE }}>
                    <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
                  </span>
                  <div>
                    <p className="text-base font-extrabold tracking-tight" style={{ color: SOVEREIGN.title }}>{title}</p>
                    {subtitle ? <p className="text-[11px]" style={{ color: SOVEREIGN.kpiCaption }}>{subtitle}</p> : null}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="rounded-lg p-1.5 transition hover:bg-[rgba(11,59,46,0.06)]"
                  style={{ color: SOVEREIGN.kpiCaption }}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="relative flex-1 overflow-y-auto px-6 py-5">{children}</div>
              <div
                className="relative flex shrink-0 items-center justify-end gap-2.5 px-6 py-4"
                style={{ borderTop: '1px solid rgba(11,59,46,0.14)', background: 'rgba(242,234,219,0.6)' }}
              >
                {footer}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
